import { useApiClient } from "@frontend/hooks/use-api"
import type { HangingProtocol } from "@shared/lib/hanging-protocol"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"

export interface HangingProtocolSlotInput {
  viewer_index: number
  text_contains?: string | null
}

export interface CreateHangingProtocolInput {
  name: string
  layout_key: string
  is_default?: boolean
  slots: HangingProtocolSlotInput[]
}

export interface UpdateHangingProtocolInput {
  name?: string
  layout_key?: string
  is_default?: boolean
  slots?: HangingProtocolSlotInput[]
}

interface HangingProtocolListResponse {
  data: HangingProtocol[]
}

function protocolsKey(projectId: string) {
  return ["hanging-protocols", projectId]
}

export function useHangingProtocols(projectId: string) {
  const api = useApiClient()
  const queryClient = useQueryClient()
  const key = useMemo(() => protocolsKey(projectId), [projectId])
  const basePath = `/projects/${projectId}/hanging-protocols`

  const query = useQuery<HangingProtocolListResponse>({
    queryKey: key,
    enabled: Boolean(projectId),
    queryFn: () => api(basePath)
  })

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: key })
  }

  const create = useMutation<HangingProtocol, Error, CreateHangingProtocolInput>({
    mutationFn: (payload) =>
      api(basePath, {
        method: "POST",
        body: JSON.stringify(payload)
      }),
    onSuccess: invalidate
  })

  const update = useMutation<HangingProtocol, Error, { protocolId: string; payload: UpdateHangingProtocolInput }>({
    mutationFn: ({ protocolId, payload }) =>
      api(`${basePath}/${protocolId}`, {
        method: "PATCH",
        body: JSON.stringify(payload)
      }),
    onSuccess: invalidate
  })

  const remove = useMutation<{ success: boolean }, Error, { protocolId: string }>({
    mutationFn: ({ protocolId }) =>
      api(`${basePath}/${protocolId}`, {
        method: "DELETE"
      }),
    onSuccess: invalidate
  })

  return {
    protocols: query.data?.data ?? [],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
    createProtocol: create,
    updateProtocol: update,
    deleteProtocol: remove
  }
}
